import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { extrairErro } from '../services/api';
import { enviarEvidencias } from '../services/evidencias';
import FormDenuncia from '../components/FormDenuncia';
import StatusCard from '../components/StatusCard';
import { IconArrowLeft, IconAlert } from '../components/icons';
import { RELACOES_GV } from '../constants/opcoes';

function DenunciaIdentificada() {
  const navigate = useNavigate();
  const location = useLocation();
  const unidadePre = location.state?.unidadePre;
  const [categorias, setCategorias] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [consentimento, setConsentimento] = useState(false);
  const [identificacao, setIdentificacao] = useState({
    nome: '', email: '', telefone: '', relacao_gv: '', cargo: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, unidRes] = await Promise.all([
          api.get('/public/categorias', { params: { tipo: 'denuncia' } }),
          api.get('/public/unidades')
        ]);
        setCategorias(catRes.data);
        setUnidades(unidRes.data);
      } catch (err) {
        setError('Erro ao carregar dados. Tente novamente.');
        console.error(err);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (formData) => {
    if (!identificacao.nome || !identificacao.email || !identificacao.relacao_gv) {
      setError('Nome, e-mail e relação com o Grupo Vilarouca são obrigatórios');
      return;
    }

    if (!consentimento) {
      setError('Você deve concordar com a Política de Privacidade para enviar a denúncia');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { evidencias, ...dados } = formData;
      const res = await api.post('/public/manifestacoes', {
        ...dados,
        ...identificacao,
        tipo: 'denuncia',
        anonima: false,
        origem: 'portal'
      });
      const { protocolo, pin } = res.data;

      // Registrar consentimento com a Política de Privacidade
      try {
        await api.post('/public/lgpd/consentimento', {
          protocolo,
          pin,
          tipo_consentimento: 'politica_privacidade',
          consentiu: true
        });
      } catch (err) {
        console.error('Erro ao registrar consentimento:', err);
        // Não bloqueia o fluxo se o consentimento não for registrado
      }

      try {
        await enviarEvidencias(protocolo, pin, evidencias);
      } catch (err) {
        // A denúncia já está registrada: mostrar o protocolo importa mais que o anexo.
        setError('Denúncia registrada, mas houve falha ao enviar os anexos.');
        console.error(err);
      }

      setResultado({ protocolo, pin });
    } catch (err) {
      setError(extrairErro(err, 'Erro ao enviar denúncia'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (resultado) {
    return (
      <div className="container">
        <button className="btn-back" onClick={() => navigate('/')}><IconArrowLeft /> Início</button>
        <StatusCard
          titulo="Denúncia recebida com sucesso"
          protocolo={resultado.protocolo}
          pin={resultado.pin}
        />
      </div>
    );
  }

  return (
    <div className="container">
      <button className="btn-back" onClick={() => navigate('/')}><IconArrowLeft /> Início</button>
      <div className="step-heading">
        <p className="eyebrow">Ouvir · Proteger · Construir juntos</p>
        <h2>Denúncia identificada</h2>
        <p className="info-text">Sua informação de contato será usada apenas para acompanhamento.</p>
      </div>

      <div className="form-section">
        <h3>Seus dados</h3>
        {error && <div className="error-message"><IconAlert />{error}</div>}
        <div className="form">
          <input
            type="text"
            placeholder="Nome completo"
            value={identificacao.nome}
            onChange={(e) => setIdentificacao({ ...identificacao, nome: e.target.value })}
            required
          />
          <input
            type="email"
            placeholder="E-mail"
            value={identificacao.email}
            onChange={(e) => setIdentificacao({ ...identificacao, email: e.target.value })}
            required
          />
          <input
            type="tel"
            placeholder="Telefone (opcional)"
            value={identificacao.telefone}
            onChange={(e) => setIdentificacao({ ...identificacao, telefone: e.target.value })}
          />
          <select
            value={identificacao.relacao_gv}
            onChange={(e) => setIdentificacao({ ...identificacao, relacao_gv: e.target.value })}
            required
          >
            <option value="">Relação com o Grupo Vilarouca</option>
            {RELACOES_GV.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <input
            type="text"
            placeholder="Cargo (opcional)"
            value={identificacao.cargo}
            onChange={(e) => setIdentificacao({ ...identificacao, cargo: e.target.value })}
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Conformidade com LGPD</h3>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={consentimento}
            onChange={(e) => setConsentimento(e.target.checked)}
            required
          />
          <span>
            Concordo com a <a href="/privacidade" target="_blank" rel="noopener noreferrer">Política de Privacidade</a> e
            os <a href="/termos" target="_blank" rel="noopener noreferrer">Termos de Uso</a> do Canal de Ética
          </span>
        </label>
        <p className="help-text">
          Sua identidade será protegida com criptografia AES-256 e apenas pessoas autorizadas terão acesso aos seus dados.
        </p>
      </div>

      <FormDenuncia
        categorias={categorias}
        unidades={unidades}
        onSubmit={handleSubmit}
        loading={loading}
        anonima={false}
        unidadePreSelecionada={unidadePre}
      />
    </div>
  );
}

export default DenunciaIdentificada;
